/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type AzureSwitchTenantModalProps = BasicModalProps<object>;

const messages = defineMessages({
    title: {
        id: 'portal.azureSwitchTenantModal.title',
        defaultMessage: 'Switch Azure directory'
    },
    luisDescription: {
        id: 'portal.azureSwitchTenantModal.luisDescription',
        defaultMessage:
            'Switching your Azure directory will change which authoring resources and apps are shown in the portal. You can switch back at any time.'
    },
    studioDescription: {
        id: 'portal.azureSwitchTenantModal.studioDescription',
        defaultMessage:
            'Switching your Azure directory will change which language resources and projects are shown in the studio. You can switch back at any time.'
    },
    azureDirectoryLabel: {
        id: 'portal.azureSwitchTenantModal.azureDirectoryLabel',
        defaultMessage: 'Azure directory'
    },
    azureDirectoryPlaceholder: {
        id: 'portal.azureSwitchTenantModal.azureDirectoryPlaceholder',
        defaultMessage: 'Select Azure directory ...'
    },
    switch: {
        id: 'portal.azureSwitchTenantModal.switch',
        defaultMessage: 'Switch'
    }
});

/**
 * Represents a modal to switch the azure tenant.
 */
export const AzureSwitchTenantModal = withModalMounter(
    observer((props: AzureSwitchTenantModalProps) => {
        const { onSubmit } = props;
        const { localize } = useLocalization();
        const { rootStore, dispatcher, uiStore } = useGlobalState();
        const tracker = useProgressTracker({ onComplete: onSubmit });

        const { tenantId } = rootStore.azureStore.session;
        const { tenantStoreHouse } = rootStore.azureStore;
        const isLanguageStudio = uiStore.portal.kind === 'languageStudio';
        const [selectedTenantId, setSelectedTenantId] = React.useState<string>(tenantId);

        React.useEffect(() => dispatcher.dispatch('getAzureTenants'), []);

        const handleSubmit = () => dispatcher.dispatch('switchAzureTenantInSession', { id: selectedTenantId, tracker });

        return (
            <BasicModal
                {...props}
                onSubmit={handleSubmit}
                disabled={tracker.isRunning}
                valid={tenantId !== selectedTenantId}
                doneButtonText={localize(messages.switch)}
                dialogContentProps={{
                    title: localize(messages.title),
                    styles: { inner: { overflowY: 'unset' } },
                    subText: localize(isLanguageStudio ? messages.studioDescription : messages.luisDescription)
                }}>
                <LuisDropdown
                    required
                    selectedKey={selectedTenantId}
                    isLoading={tenantStoreHouse.tracker.isRunning}
                    label={localize(messages.azureDirectoryLabel)}
                    placeholder={localize(messages.azureDirectoryPlaceholder)}
                    onChange={(_, item) => setSelectedTenantId(item.key as string)}
                    options={tenantStoreHouse.items.map(item => ({ key: item.id, text: `${item.name} (${item.id})` }))}
                />
            </BasicModal>
        );
    })
);
